import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { subscribe, write } from '@/lib/firebase';
import { DEFAULT_DATA } from '@/shared/constants/config';
import { reportToSentry } from '@/lib/sentry';
import { useHouseholdContext } from './HouseholdProvider';

const HouseholdDataContext = createContext(null);

export function HouseholdDataProvider({ children }) {
  const { householdId } = useHouseholdContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!householdId) { setLoading(false); setData(null); return; }
    setLoading(true);
    const unsub = subscribe('households/' + householdId + '/budget', (val) => {
      if (val) {
        const n = { ...DEFAULT_DATA, ...val };
        if (val.txns && !Array.isArray(val.txns)) n.txns = Object.values(val.txns);
        if (val.bills && !Array.isArray(val.bills)) n.bills = Object.values(val.bills);
        setData(n);
      } else {
        setData(DEFAULT_DATA);
      }
      setLoading(false);
    });
    return () => unsub();
  }, [householdId]);

  const save = useCallback(async (nd) => {
    if (!householdId) return;
    setData(nd);
    try {
      await write('households/' + householdId + '/budget', nd);
    } catch (e) {
      console.error('Save error:', e);
      reportToSentry(e, { op: 'householdData.save', householdId });
    }
  }, [householdId]);

  const clearData = useCallback(() => setData(null), []);

  return (
    <HouseholdDataContext.Provider value={{ data, loading, save, clearData, householdId }}>
      {children}
    </HouseholdDataContext.Provider>
  );
}

export function useHouseholdData() {
  const ctx = useContext(HouseholdDataContext);
  if (!ctx) throw new Error('useHouseholdData must be used within HouseholdDataProvider');
  return ctx;
}
